import { useEffect, useRef, useState } from "react";
import { fileUrl } from "@/lib/api";
import { normalizeMedia, packMedia, videoEmbed, uploadMediaFile, parseTime } from "@/lib/media";
import { Upload, Loader2, X, ChevronDown, ChevronRight, Film, Smartphone, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import MediaPicker from "./MediaPicker";

const formatSeconds = (s) => {
  const n = Number(s) || 0;
  if (n <= 0) return "";
  const m = Math.floor(n / 60);
  const r = n % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
};

function MediaSlot({ kind, value, onChange, placeholder, testId }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileRef = useRef(null);
  const isVideo = kind === "video";
  const embed = isVideo && value ? videoEmbed(value) : null;

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setProgress(0);
    try {
      const url = await uploadMediaFile(f, setProgress);
      onChange(url);
      toast.success("Uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      {value ? (
        <div className="relative w-20 h-20 bg-zinc-100 overflow-hidden rounded-md group shrink-0">
          {isVideo ? (
            embed ? (
              <div className="w-full h-full flex items-center justify-center text-zinc-500"><Film className="w-5 h-5" /></div>
            ) : (
              <video src={fileUrl(value)} muted playsInline preload="metadata" className="w-full h-full object-cover" />
            )
          ) : (
            <img src={fileUrl(value)} alt="" className="w-full h-full object-cover" />
          )}
          <button onClick={() => onChange("")} type="button" className="absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100" aria-label="Remove" data-testid={`${testId}-remove`}>
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div className="w-20 h-20 bg-zinc-100 rounded-md flex items-center justify-center text-zinc-400 shrink-0">
          {isVideo ? <Film className="w-5 h-5" /> : <Upload className="w-5 h-5" />}
        </div>
      )}
      <div className="flex-1 space-y-2">
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full text-sm border border-zinc-300 rounded-md px-3 py-2"
          data-testid={`${testId}-url`}
        />
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => fileRef.current?.click()} className="text-xs px-3 py-2 border border-zinc-300 rounded-md hover:bg-zinc-50 inline-flex items-center gap-2" disabled={uploading} data-testid={`${testId}-upload`}>
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            {uploading ? `Uploading… ${progress}%` : isVideo ? "Upload video" : "Upload"}
          </button>
          <input ref={fileRef} type="file" accept={isVideo ? "video/*" : "image/*"} className="hidden" onChange={onFile} />
          {!isVideo && (
            <button type="button" onClick={() => setPickerOpen(true)} className="text-xs px-3 py-2 border border-zinc-300 rounded-md hover:bg-zinc-50 inline-flex items-center gap-2" data-testid={`${testId}-library`}>
              <FolderOpen className="w-3 h-3" /> Library
            </button>
          )}
        </div>
      </div>
      {!isVideo && <MediaPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={(url) => onChange(url)} />}
    </div>
  );
}

// Media picker: desktop/mobile images + desktop/mobile background videos.
// Value is a plain string (image URL) or {image, image_mobile, video, video_mobile}.
export default function ImageUploader({ value, onChange, label = "Image", testId = "image-uploader", imageOnly = false }) {
  const m = normalizeMedia(value);
  const hasExtras = !!(m.image_mobile || m.video || m.video_mobile || m.overlay);
  const [open, setOpen] = useState(hasExtras);
  useEffect(() => { if (hasExtras) setOpen(true); }, [hasExtras]);

  const set = (k) => (v) => onChange(packMedia({ ...normalizeMedia(value), [k]: v }));

  return (
    <div className="space-y-2" data-testid={testId}>
      {label && <label className="text-xs uppercase tracking-wider text-zinc-500">{label}</label>}
      <MediaSlot kind="image" value={m.image} onChange={imageOnly ? (v) => onChange(v) : set("image")} placeholder="https://... or upload" testId={testId} />
      {!imageOnly && (
        <div className="border border-zinc-200 rounded-md">
          <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-600 hover:bg-zinc-50" data-testid={`${testId}-advanced-toggle`}>
            {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            <Smartphone className="w-3.5 h-3.5" /> Mobile image
            <span className="text-zinc-300">·</span>
            <Film className="w-3.5 h-3.5" /> Video
            <span className="text-zinc-300">·</span>
            <span>Overlay</span>
            {hasExtras && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-500" />}
          </button>
          {open && (
            <div className="px-3 pb-3 pt-1 space-y-4">
              <div>
                <div className="text-[11px] text-zinc-500 mb-1.5">Overlay darkness — darkens the image so text on top stays readable</div>
                <div className="flex items-center gap-3">
                  <select
                    value={String(m.overlay || 0)}
                    onChange={(e) => set("overlay")(Number(e.target.value))}
                    className="text-sm border border-zinc-300 rounded-md px-3 py-2 bg-white"
                    data-testid={`${testId}-overlay`}
                  >
                    <option value="0">None (default)</option>
                    <option value="15">Light — 15%</option>
                    <option value="25">Soft — 25%</option>
                    <option value="35">Medium — 35%</option>
                    <option value="45">Strong — 45%</option>
                    <option value="60">Heavy — 60%</option>
                    <option value="75">Very heavy — 75%</option>
                  </select>
                  {m.image && (
                    <div className="relative w-20 h-12 rounded-md overflow-hidden shrink-0">
                      <img src={fileUrl(m.image)} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black" style={{ opacity: (m.overlay || 0) / 100 }} />
                      <div className="absolute inset-0 flex items-center justify-center text-white text-[9px] tracking-widest">TEXT</div>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <div className="text-[11px] text-zinc-500 mb-1.5">Mobile image (optional — desktop image is auto-optimized for mobile if empty)</div>
                <MediaSlot kind="image" value={m.image_mobile} onChange={set("image_mobile")} placeholder="Mobile image URL or upload" testId={`${testId}-mobile`} />
              </div>
              <div>
                <div className="text-[11px] text-zinc-500 mb-1.5">Video (optional — replaces the image; plays muted, looping, no controls). YouTube / Vimeo / .mp4 link or upload.</div>
                <MediaSlot kind="video" value={m.video} onChange={set("video")} placeholder="YouTube / Vimeo / .mp4 URL or upload" testId={`${testId}-video`} />
              </div>

              {m.video && /(youtube\.com|youtu\.be|vimeo\.com)/.test(m.video) && (
                <div>
                  <div className="text-[11px] text-zinc-500 mb-1.5">Play only a segment of the video (optional). Accepts seconds (e.g. <b>45</b>) or MM:SS (e.g. <b>1:30</b>).</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Start time</div>
                      <input
                        type="text"
                        placeholder="e.g. 0:15"
                        defaultValue={m.video_start ? formatSeconds(m.video_start) : ""}
                        onBlur={(e) => set("video_start")(parseTime(e.target.value))}
                        className="w-full text-sm border border-zinc-300 rounded-md px-3 py-2 bg-white"
                        data-testid={`${testId}-video-start`}
                      />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">End time</div>
                      <input
                        type="text"
                        placeholder="e.g. 0:45"
                        defaultValue={m.video_end ? formatSeconds(m.video_end) : ""}
                        onBlur={(e) => set("video_end")(parseTime(e.target.value))}
                        className="w-full text-sm border border-zinc-300 rounded-md px-3 py-2 bg-white"
                        data-testid={`${testId}-video-end`}
                      />
                    </div>
                  </div>
                  <div className="text-[10px] text-zinc-400 mt-1">Leave blank to play the whole video. On YouTube, playback loops back to the start time — perfect for showcasing a highlight.</div>
                </div>
              )}
              <div>
                <div className="text-[11px] text-zinc-500 mb-1.5">Mobile video (optional — used on phones instead of the video above)</div>
                <MediaSlot kind="video" value={m.video_mobile} onChange={set("video_mobile")} placeholder="Mobile video URL or upload" testId={`${testId}-video-mobile`} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
